import { useCallback } from 'react';

import Decimal from 'decimal.js';

import {
  usePoolInfo,
  useUserInfo,
} from '@services/contract/bonds/rewardPool.contract';
import { useBalance } from '@services/contract/common/erc1155.contract';

import { toBN } from '@utils/formaters.utils';
import { findTokenByAddress } from '@utils/tokens.utils';

import { NFT_BONDS, REWARD_POOL } from '@constants/bonds/contracts.constants';
import { CHAIN_ID } from '@constants/common/chain.constants';
import { SECONDS_IN_YEAR } from '@constants/common/date.constants';

type NftPoolInfoParamType = {
  chainId: CHAIN_ID;
  nftPriceUsd: Decimal;
};

const REWARD_TOKEN_USD = toBN(1);

const useNftPoolInfo = ({ chainId, nftPriceUsd }: NftPoolInfoParamType) => {
  const {
    data: poolInfo,
    isPending: isPendingPoolInfo,
    refetch: refetchPoolInfo,
  } = usePoolInfo(chainId);
  const {
    data: userInfo,
    isPending: isPendingUserInfo,
    refetch: refetchUserInfo,
  } = useUserInfo(chainId);

  const { data: tvl, refetch: refetchTvl } = useBalance({
    nftAddress: NFT_BONDS,
    chainId,
    contractAddress: REWARD_POOL,
  });

  const rewardToken = poolInfo
    ? findTokenByAddress(poolInfo.rewardToken, chainId)
    : null;

  const tvlUsd = toBN(tvl || 0).times(nftPriceUsd);

  const stakeAmount = toBN(userInfo?.amount || 0);
  const stakeAmountUsd = stakeAmount.times(nftPriceUsd);

  const claimAmount = toBN(userInfo?.pendingShare || 0);
  const claimAmountUsd = claimAmount.times(REWARD_TOKEN_USD);

  const refetch = useCallback(async () => {
    await Promise.all([
      await refetchPoolInfo(),
      await refetchUserInfo(),
      await refetchTvl(),
    ]);
  }, []);

  return {
    data:
      poolInfo && rewardToken
        ? {
            apr: tvl
              ? toBN(poolInfo.sharesPerSecond, rewardToken.decimals)
                  .times(REWARD_TOKEN_USD)
                  .times(SECONDS_IN_YEAR)
                  .div(tvlUsd)
              : toBN(0),
            stakeAmount,
            stakeAmountUsd,
            claimAmount,
            claimAmountUsd,
            rewardToken,
          }
        : null,
    refetch,
    isPending: isPendingPoolInfo || isPendingUserInfo,
  };
};

export default useNftPoolInfo;
